import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, IndianRupee, AlertTriangle, Users, Loader2, X,
} from "lucide-react";

const PRICING_TYPES = [
  { value: "free", label: "Free (bundled)" },
  { value: "monthly", label: "Monthly subscription" },
  { value: "yearly", label: "Yearly subscription" },
  { value: "one_time", label: "One-time" },
  { value: "usage_based", label: "Usage-based" },
];

const EMPTY = {
  feature_key: "",
  feature_name: "",
  description: "",
  module_name: "",
  pricing_type: "monthly",
  monthly_price: 0,
  yearly_price: 0,
  one_time_price: 0,
  usage_unit: "",
  usage_price: 0,
  is_core_feature: false,
  is_active: true,
};

export default function SuperAdminFeatures() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | feature object
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Confirmation dialog state for the "disable feature" flow
  const [confirmDisable, setConfirmDisable] = useState(null);   // { feature, subscribers, monthly_mrr }
  // Subscribers drawer state
  const [subsDrawer, setSubsDrawer] = useState(null);           // { feature, items, monthly_mrr }
  const [subsLoading, setSubsLoading] = useState(false);
  const [cancellingTenant, setCancellingTenant] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/superadmin/features");
      setRows(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load features");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (r) => { setForm({ ...EMPTY, ...r }); setEditing(r); };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/superadmin/features", form);
        toast.success("Feature created");
      } else {
        await api.put(`/superadmin/features/${form.feature_key}`, form);
        toast.success("Feature updated");
      }
      close(); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (r) => {
    // Turning OFF — show impact preview first (count + MRR + grandfather note)
    if (r.is_active) {
      try {
        const { data } = await api.get(`/superadmin/features/${r.feature_key}/subscribers`);
        setConfirmDisable({
          feature: r,
          subscribers: data.total_subscribers,
          monthly_mrr: data.monthly_mrr,
          items: data.items,
        });
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load subscriber impact");
      }
      return;
    }
    // Turning ON — no preview needed
    await applyToggle(r, true);
  };

  const applyToggle = async (r, nextActive) => {
    try {
      await api.patch(`/superadmin/features/${r.feature_key}/toggle`, {
        is_active: nextActive,
      });
      toast.success(
        nextActive
          ? `${r.feature_name} turned ON — visible to new tenants again`
          : `${r.feature_name} disabled for NEW signups — existing paying tenants keep access`
      );
      setConfirmDisable(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    }
  };

  const openSubscribers = async (r) => {
    setSubsLoading(true);
    setSubsDrawer({ feature: r, items: [], monthly_mrr: 0 });
    try {
      const { data } = await api.get(`/superadmin/features/${r.feature_key}/subscribers`);
      setSubsDrawer({ feature: r, ...data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load subscribers");
      setSubsDrawer(null);
    } finally { setSubsLoading(false); }
  };

  const cancelForTenant = async (tenantId, tenantName) => {
    if (!subsDrawer) return;
    if (!window.confirm(
      `Cancel ${subsDrawer.feature.feature_name} for "${tenantName}"?\n\n` +
      `They will lose access immediately. Make sure you've already talked to them.`
    )) return;
    setCancellingTenant(tenantId);
    try {
      await api.delete(`/superadmin/features/${subsDrawer.feature.feature_key}/subscribers/${tenantId}`);
      toast.success(`Cancelled for "${tenantName}"`);
      // Refresh subscriber list in-place
      openSubscribers(subsDrawer.feature);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally { setCancellingTenant(null); }
  };

  const remove = async (key) => {
    if (!window.confirm(`Delete feature "${key}"? This cancels every tenant subscription on it.`)) return;
    try {
      await api.delete(`/superadmin/features/${key}`);
      toast.success("Feature deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="super-features-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl">Feature Catalog</h1>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">Every feature in the platform, with its price. Core features are bundled in plans; add-ons are billed separately.</p>
        </div>
        <button onClick={openNew} className="kb-btn-primary inline-flex items-center gap-2" data-testid="feature-new-btn">
          <Plus size={14} /> New feature
        </button>
      </div>

      {error && (
        <div className="border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm flex items-start gap-2" data-testid="feature-load-error">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" /> <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="bg-white border border-[hsl(var(--kb-border))] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--kb-surface-alt))] text-left">
              <tr>
                <th className="p-3">Key</th>
                <th className="p-3">Name</th>
                <th className="p-3">Type</th>
                <th className="p-3">Monthly</th>
                <th className="p-3">Yearly</th>
                <th className="p-3">Usage</th>
                <th className="p-3">Core</th>
                <th className="p-3">Active</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.feature_key} className="border-t border-[hsl(var(--kb-border))]" data-testid={`feature-row-${r.feature_key}`}>
                  <td className="p-3 font-mono text-xs">{r.feature_key}</td>
                  <td className="p-3">{r.feature_name}</td>
                  <td className="p-3"><span className="bg-[hsl(var(--kb-surface-alt))] px-2 py-0.5 text-xs">{r.pricing_type}</span></td>
                  <td className="p-3">₹{r.monthly_price || 0}</td>
                  <td className="p-3">₹{r.yearly_price || 0}</td>
                  <td className="p-3">{r.pricing_type === "usage_based" ? `₹${r.usage_price}/${r.usage_unit}` : "—"}</td>
                  <td className="p-3">{r.is_core_feature ? "✓" : "—"}</td>
                  <td className="p-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(r)}
                      className="inline-flex items-center gap-2 group"
                      title={r.is_active ? "Click to turn OFF — hide from tenants" : "Click to turn ON — visible to tenants"}
                      data-testid={`feature-toggle-${r.feature_key}`}
                    >
                      <span className={`relative w-9 h-5 rounded-full transition-colors ${r.is_active ? "bg-green-500" : "bg-[hsl(var(--kb-border))]"}`}>
                        <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${r.is_active ? "left-[18px]" : "left-0.5"}`} />
                      </span>
                      <span className={`text-xs ${r.is_active ? "text-green-700" : "text-[hsl(var(--kb-muted))]"}`}>
                        {r.is_active ? "On" : "Off"}
                      </span>
                    </button>
                  </td>
                  <td className="p-3 flex gap-2 items-center">
                    <button
                      type="button"
                      onClick={() => openSubscribers(r)}
                      className="text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-primary))] inline-flex items-center gap-1 text-xs"
                      title="View tenants subscribed to this feature"
                      data-testid={`feature-subscribers-${r.feature_key}`}
                    >
                      <Users size={12} />
                    </button>
                    <button onClick={() => openEdit(r)} className="text-[hsl(var(--kb-primary))]" data-testid={`feature-edit-${r.feature_key}`}><Pencil size={14} /></button>
                    <button onClick={() => remove(r.feature_key)} className="text-red-600" data-testid={`feature-delete-${r.feature_key}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={close}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="feature-dialog">
            <h2 className="font-display text-2xl">{editing === "new" ? "New feature" : "Edit feature"}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Feature key <span className="text-red-500">*</span></label>
                <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1 font-mono"
                  value={form.feature_key} readOnly={editing !== "new"}
                  onChange={(e) => setForm({ ...form, feature_key: e.target.value.toLowerCase() })}
                  data-testid="feature-key-input" />
              </div>
              <div>
                <label className="text-sm font-medium">Display name <span className="text-red-500">*</span></label>
                <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.feature_name}
                  onChange={(e) => setForm({ ...form, feature_name: e.target.value })}
                  data-testid="feature-name-input" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Description</label>
                <textarea rows={2} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.description || ""}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Module</label>
                <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.module_name || ""}
                  onChange={(e) => setForm({ ...form, module_name: e.target.value })} />
              </div>
              <div>
                <label className="text-sm font-medium">Pricing type</label>
                <select className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.pricing_type}
                  onChange={(e) => setForm({ ...form, pricing_type: e.target.value })}
                  data-testid="feature-pricing-type">
                  {PRICING_TYPES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Monthly price (₹)</label>
                <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.monthly_price}
                  onChange={(e) => setForm({ ...form, monthly_price: Number(e.target.value) })}
                  data-testid="feature-monthly-price" />
              </div>
              <div>
                <label className="text-sm font-medium">Yearly price (₹)</label>
                <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.yearly_price}
                  onChange={(e) => setForm({ ...form, yearly_price: Number(e.target.value) })} />
              </div>
              <div>
                <label className="text-sm font-medium">One-time price (₹)</label>
                <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                  value={form.one_time_price}
                  onChange={(e) => setForm({ ...form, one_time_price: Number(e.target.value) })} />
              </div>
              {form.pricing_type === "usage_based" && (
                <>
                  <div>
                    <label className="text-sm font-medium">Usage unit</label>
                    <input className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1" placeholder="GB / order / seat"
                      value={form.usage_unit || ""}
                      onChange={(e) => setForm({ ...form, usage_unit: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Price per unit (₹)</label>
                    <input type="number" min={0} className="w-full border border-[hsl(var(--kb-border))] p-2 text-sm mt-1"
                      value={form.usage_price}
                      onChange={(e) => setForm({ ...form, usage_price: Number(e.target.value) })} />
                  </div>
                </>
              )}
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_core_feature} onChange={(e) => setForm({ ...form, is_core_feature: e.target.checked })} /> Core feature (bundled in plans only)</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Active</label>
            </div>
            <div className="flex gap-2 justify-end pt-3 border-t border-[hsl(var(--kb-border))]">
              <button className="kb-btn-outline" onClick={close}>Cancel</button>
              <button className="kb-btn-primary inline-flex items-center gap-2" onClick={save} disabled={saving || !form.feature_key || !form.feature_name} data-testid="feature-save-btn">
                <IndianRupee size={14} /> {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm disable — impact preview */}
      {confirmDisable && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setConfirmDisable(null)}
          data-testid="confirm-disable-dialog"
        >
          <div
            className="bg-white max-w-lg w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
                <AlertTriangle size={20} className="text-amber-700" />
              </div>
              <div>
                <h3 className="font-display text-xl">Disable {confirmDisable.feature.feature_name}?</h3>
                <p className="text-sm text-[hsl(var(--kb-muted))] mt-1">
                  This <b>hides the feature from new tenants</b>. Existing paying subscribers <b>keep their access</b> — you'll need to cancel them individually (via the Subscribers list) if you want to fully discontinue the feature.
                </p>
              </div>
            </div>

            <div className="bg-[hsl(var(--kb-surface-alt))] border border-[hsl(var(--kb-border))] p-4 grid grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Current subscribers</div>
                <div className="font-display text-3xl mt-1" data-testid="confirm-disable-subs">{confirmDisable.subscribers}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Monthly recurring</div>
                <div className="font-display text-3xl mt-1" data-testid="confirm-disable-mrr">₹{(confirmDisable.monthly_mrr || 0).toLocaleString()}</div>
              </div>
            </div>

            {confirmDisable.subscribers > 0 && (
              <div className="text-xs text-[hsl(var(--kb-muted))]">
                These tenants will see the feature in their billing page as "Subscribed (legacy)". They'll keep using it until they cancel or you cancel for them.
              </div>
            )}

            <div className="flex justify-end gap-2 pt-3 border-t border-[hsl(var(--kb-border))]">
              <button
                className="kb-btn-outline"
                onClick={() => setConfirmDisable(null)}
                data-testid="confirm-disable-cancel"
              >
                Keep enabled
              </button>
              <button
                className="kb-btn-primary inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700"
                onClick={() => applyToggle(confirmDisable.feature, false)}
                data-testid="confirm-disable-confirm"
              >
                Disable for new signups
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscribers drawer */}
      {subsDrawer && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setSubsDrawer(null)}
          data-testid="subscribers-drawer"
        >
          <div
            className="bg-white max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-xl flex items-center gap-2"><Users size={18} /> Subscribers — {subsDrawer.feature.feature_name}</h3>
                <p className="text-xs text-[hsl(var(--kb-muted))] mt-1 font-mono">{subsDrawer.feature.feature_key}</p>
              </div>
              <button onClick={() => setSubsDrawer(null)} className="text-[hsl(var(--kb-muted))]"><X size={18} /></button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[hsl(var(--kb-surface-alt))] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Total subscribers</div>
                <div className="font-display text-2xl mt-1">{subsDrawer.total_subscribers || 0}</div>
              </div>
              <div className="bg-[hsl(var(--kb-surface-alt))] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Monthly recurring</div>
                <div className="font-display text-2xl mt-1">₹{(subsDrawer.monthly_mrr || 0).toLocaleString()}</div>
              </div>
            </div>

            {subsLoading ? (
              <div className="text-center py-8 text-[hsl(var(--kb-muted))]"><Loader2 size={20} className="animate-spin inline" /></div>
            ) : (subsDrawer.items || []).length === 0 ? (
              <div className="text-center py-8 text-sm text-[hsl(var(--kb-muted))]" data-testid="subscribers-empty">
                No tenants are currently paying for this feature.
              </div>
            ) : (
              <div className="border border-[hsl(var(--kb-border))] divide-y divide-[hsl(var(--kb-border))]" data-testid="subscribers-list">
                {subsDrawer.items.map((s) => (
                  <div key={s.subscription_id} className="px-3 py-2 flex items-center gap-3" data-testid={`subscriber-row-${s.tenant_slug}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{s.tenant_name || s.tenant_slug}</div>
                      <div className="text-xs text-[hsl(var(--kb-muted))] font-mono">
                        /t/{s.tenant_slug}/ · {s.billing_cycle} · ₹{s.price_at_purchase}/period
                        {s.cycle_end_date && (
                          <> · renews {new Date(s.cycle_end_date).toLocaleDateString()}</>
                        )}
                      </div>
                    </div>
                    <span className="text-xs px-2 py-0.5 bg-green-100 text-green-800">{s.tenant_status}</span>
                    <button
                      type="button"
                      disabled={cancellingTenant === s.tenant_id}
                      onClick={() => cancelForTenant(s.tenant_id, s.tenant_name || s.tenant_slug)}
                      className="text-xs inline-flex items-center gap-1 px-2 py-1 border border-red-300 bg-red-50 text-red-700 hover:bg-red-100"
                      data-testid={`subscriber-cancel-${s.tenant_slug}`}
                    >
                      {cancellingTenant === s.tenant_id ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
                      Cancel
                    </button>
                  </div>
                ))}
              </div>
            )}

            <p className="text-[10px] text-[hsl(var(--kb-muted))] border-t border-[hsl(var(--kb-border))] pt-3">
              Tip: talk to each subscriber before cancelling. They'll lose access immediately and you'll need to refund manually.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
